import React from 'react';

import dayjs from '~/timeInit';

import { Alert, Box, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { TourFormDeparture } from '~/services/ToursService';

import { DepartureInventory } from '~/types/tours/inventory';

import DepartureInventoryPanel from './DepartureInventoryPanel';
import { ScalePricingItem } from './departureInventory';

interface Props {
  /** The tour's departures, chronologically, which a whole-tour scale prices all of. */
  departures: Array<TourFormDeparture>;
  selectedDepartureId: string;
  onSelectDeparture: (departureId: string) => void;
  inventory?: DepartureInventory;
  /** The service's sliding scale, which applies to every departure alike. */
  items: Array<ScalePricingItem>;
  isLoading: boolean;
  error?: { message: string } | null;
}

function formatDate(date?: string): string {
  return date ? dayjs(date).format('D MMM YYYY') : '';
}

/**
 * Inventory for a service priced with one scale across the whole tour.
 *
 * The departure is chosen here but held by the page, because the price table
 * above marks where the chosen departure sits - so both are reading from one
 * selection rather than each keeping their own.
 */
export default function TourInventorySection({
  departures,
  selectedDepartureId,
  onSelectDeparture,
  inventory,
  items,
  isLoading,
  error,
}: Props) {
  if (!isLoading && !departures.length) {
    return <Alert severity="info">This tour has no upcoming departures, so there is no inventory to track.</Alert>;
  }

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <TextField
        select
        label="Departure"
        value={selectedDepartureId}
        onChange={(event) => onSelectDeparture(event.target.value)}
        sx={{ minWidth: 420, maxWidth: 560 }}
        // The empty value still renders text, so the label has to stay shrunk -
        // left to itself it drops back over "No departure selected".
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
          displayEmpty: true,
          renderValue: (value) => {
            const departure = departures.find((option) => option.departureId === value);

            return departure
              ? `${departure.departureName} · ${formatDate(departure.departureDate)}`
              : 'No departure selected';
          },
        }}
      >
        {/* Choosing none is what leaves the price table above unmarked, for
            reading the scale on its own. */}
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">
            No departure — show the scale on its own
          </Typography>
        </MenuItem>

        {departures.map((departure) => (
          <MenuItem key={departure.departureId} value={departure.departureId}>
            <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ minWidth: 116 }}>
                {departure.departureName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(departure.departureDate)}
              </Typography>
              <Box flex={1} />
            </Stack>
          </MenuItem>
        ))}
      </TextField>

      {selectedDepartureId ? (
        <DepartureInventoryPanel inventory={inventory} items={items} isLoading={isLoading} error={error} />
      ) : (
        <Alert severity="info">Choose a departure to see what has been sold against it.</Alert>
      )}
    </Stack>
  );
}
